import * as React from 'react';
import { Link } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore @types/react-lines-ellipsis does not exist
import * as HTMLEllipsis from 'react-lines-ellipsis/lib/html';
import { UitkLink } from 'uitk-react-link';
import { UitkCard, UitkCardLink, UitkCardContentSection } from 'uitk-react-cards';
import { UitkCarousel } from 'uitk-react-carousel';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { getSeeAllURL } from '../../../../helpers/common-helper';
import { onClickScrollToTarget } from '../../../../helpers/smooth-scroll';
import { ActivityDestinationViewSearch } from '../../../../common/__generated__/api';
import { useStores } from '../../../../utils/hooks/useStores';

export interface SlimCategoryCarouselProps {
  categoryGroup: ActivityDestinationViewSearch.ActivityGroups;
}

export const SlimCategoryCarousel: React.FC<SlimCategoryCarouselProps> = ({ categoryGroup }): JSX.Element | null => {
  const { routeStore, analytics } = useStores();

  const handleAnalytics = (
    categoryAnalytics: ActivityDestinationViewSearch.Analytics,
    isCategoryAction: boolean
  ): void | null => {
    if (!categoryAnalytics) {
      return null;
    }
    if (isCategoryAction) {
      onClickScrollToTarget({ scrollTo: 0 });
    }
    analytics.trackEvent(categoryAnalytics.referrerId, categoryAnalytics.linkName, analytics.omnitureData);
  };

  const renderCategoryCard = (
    category: ActivityDestinationViewSearch.ActivityBasicTileInlineFragment,
    index: number
  ): JSX.Element | null => {
    if (!category.name) {
      return null;
    }

    return (
      <UitkCard
        key={`slim-category-carousel-card-${index}`}
        data-testid={`slim-category-carousel-card-${index}`}
        tag="li"
        border={true}
      >
        <UitkLayoutFlex direction="column" justifyContent="space-between" className="slim-category-details">
          <UitkCardContentSection padded={['inlineend', 'inlinestart', 'blockstart']}>
            <HTMLEllipsis
              className="uitk-type-300 uitk-type-bold LinesEllipsis type-gray"
              aria-hidden="true"
              unsafeHTML={category.name}
              maxLine="2"
              ellipsis="..."
            />
          </UitkCardContentSection>
          <UitkCardContentSection padded={['inlineend', 'inlinestart', 'blockend']}>
            {category.secondaryMessage ? (
              <HTMLEllipsis
                className="uitk-type-200 uitk-type-regular"
                aria-hidden="true"
                unsafeHTML={category.secondaryMessage}
                maxLine="1"
                ellipsis="..."
              />
            ) : null}
          </UitkCardContentSection>
        </UitkLayoutFlex>
        <UitkCardLink>
          <Link
            onClick={handleAnalytics.bind(null, category.analytics, true)}
            to={getSeeAllURL(category.link, routeStore.queryParams)}
          >
            {category.name}
          </Link>
        </UitkCardLink>
      </UitkCard>
    );
  };

  const renderShowAllThingsToDoLink = (action: ActivityDestinationViewSearch.__Action): JSX.Element => (
    <UitkLink data-testid="slim-category-carousel-show-all-things-to-do--link">
      <Link to={action.url} onClick={handleAnalytics.bind(null, action.analytics, false)}>
        {action.label}
      </Link>
    </UitkLink>
  );

  const { activityTiles, action } = categoryGroup;

  const CategoryCarousel = (classname: string): JSX.Element => (
    <section id="slim-category-list" className={classname} data-testid="slim-category-carousel--section">
      <UitkCarousel
        carouselName="slim-category-carousel"
        itemsVisible={{
          lg: 6,
          md: 5,
          sm: 2
        }}
        pageBy={1}
        peekWidth={{
          lg: 24,
          md: 24,
          sm: 90
        }}
        buttonText={{
          nextButton: 'next',
          prevButton: 'previous'
        }}
        navigationIconId={{
          next: 'slim-category-carousel-next-button-title',
          prev: 'slim-category-carousel-prev-button-title'
        }}
        wrapItems={false}
      >
        {activityTiles.map((category, index) =>
          renderCategoryCard(category as ActivityDestinationViewSearch.ActivityBasicTileInlineFragment, index)
        )}
      </UitkCarousel>
      {action && renderShowAllThingsToDoLink(action)}
    </section>
  );

  return activityTiles && activityTiles.length > 0 ? (
    <Viewport>
      <ViewLarge>{CategoryCarousel('all-t-margin-four all-x-margin-three slim-category-carousel-desktop')}</ViewLarge>
      <ViewSmall>{CategoryCarousel('all-t-margin-four all-x-margin-three slim-category-carousel-mobile')}</ViewSmall>
    </Viewport>
  ) : null;
};
